import React, { useState } from "react";
import { Alert, Button, Form as BTForm, Modal, Spinner } from "react-bootstrap";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import axios from "src/config/axios";
import FormSelectBox from "src/components/Forms/FormSelectBox";
import FormPriceInput from "src/components/Forms/FormPriceInput";
import GeneralFormError from "src/components/Forms/GeneralFormError";
import { dateFormatter, euroFormatter } from "src/global";

const CopyPricesDialog = ({ seasons, handleClose, handleSuccess }) => {
  const { t } = useTranslation("dashboard");
  const [preview, setPreview] = useState(null);
  const replacementCount =
    preview?.filter((price) => price.existing_price !== null).length ?? 0;
  const seasonOptions = seasons.map((season) => ({
    id: season.id,
    name: `${season.name} (${dateFormatter(season.from)} – ${dateFormatter(
      season.to
    )})`,
  }));

  function validate(values) {
    const errors = {};

    if (!values.source_season_id) {
      errors.source_season_id = t(
        "settings.price_list.prices.copy_prices_dialog.validation.source_season_required"
      );
    }
    if (!values.target_season_id) {
      errors.target_season_id = t(
        "settings.price_list.prices.copy_prices_dialog.validation.target_season_required"
      );
    } else if (values.source_season_id === values.target_season_id) {
      errors.target_season_id = t(
        "settings.price_list.prices.copy_prices_dialog.validation.different_seasons"
      );
    }

    if (preview !== null) {
      const priceErrors = values.prices.map((price) => {
        const amount = String(price.price).trim().replace(",", ".");
        return amount === "" ||
          !Number.isFinite(Number(amount)) ||
          Number(amount) < 0
          ? {
              price: t(
                "settings.price_list.prices.copy_prices_dialog.validation.invalid_amount"
              ),
            }
          : {};
      });
      if (priceErrors.some((error) => error.price)) errors.prices = priceErrors;
      if (replacementCount > 0 && !values.replacement_confirmed) {
        errors.replacement_confirmed = t(
          "settings.price_list.prices.copy_prices_dialog.validation.replacement_confirmation_required"
        );
      }
    }

    return errors;
  }

  async function submit(values, helpers) {
    try {
      if (preview === null) {
        const response = await axios.get("/api/v1/prices/copy_preview.json", {
          params: {
            "price_copy[source_season_id]": values.source_season_id,
            "price_copy[target_season_id]": values.target_season_id,
          },
        });
        helpers.setValues({
          ...values,
          prices: response.data.entries.map((price) => ({
            id: price.id,
            price: price.price,
          })),
          replacement_confirmed: false,
        });
        setPreview(response.data.entries);
      } else {
        await axios.post("/api/v1/prices/copy.json", {
          price_copy: {
            source_season_id: values.source_season_id,
            target_season_id: values.target_season_id,
            prices: values.prices.map((price) => ({
              ...price,
              price: String(price.price).replace(",", "."),
            })),
          },
        });
        handleSuccess(values.target_season_id);
      }
    } catch (error) {
      helpers.setErrors(
        error.response?.data || {
          base: t(
            "settings.price_list.prices.copy_prices_dialog.request_error"
          ),
        }
      );
    } finally {
      helpers.setSubmitting(false);
    }
  }

  return (
    <Formik
      initialValues={{
        source_season_id: "",
        target_season_id: "",
        prices: [],
        replacement_confirmed: false,
      }}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={submit}
    >
      {(helpers) => {
        const { values, errors, isSubmitting, handleChange } = helpers;
        const close = isSubmitting ? undefined : handleClose;

        return (
          <Modal
            show={true}
            onHide={close}
            size="lg"
            backdrop={isSubmitting ? "static" : true}
            keyboard={!isSubmitting}
          >
            <Modal.Header>
              <Modal.Title>
                {t("settings.price_list.prices.copy_prices_dialog.title")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form id="copy-prices-form" autoComplete="off">
                <GeneralFormError errors={errors} />
                {preview === null ? (
                  <>
                    <p>
                      {t(
                        "settings.price_list.prices.copy_prices_dialog.description"
                      )}
                    </p>
                    <FormSelectBox
                      name="source_season_id"
                      label={t(
                        "settings.price_list.prices.copy_prices_dialog.source_season.label"
                      )}
                      options={seasonOptions}
                      option_value="id"
                      option_name="name"
                      disabled={isSubmitting}
                      required
                    />
                    <FormSelectBox
                      name="target_season_id"
                      label={t(
                        "settings.price_list.prices.copy_prices_dialog.target_season.label"
                      )}
                      options={seasonOptions.filter(
                        (season) =>
                          String(season.id) !== String(values.source_season_id)
                      )}
                      option_value="id"
                      option_name="name"
                      disabled={isSubmitting}
                      required
                    />
                    <p className="text-muted mb-0">
                      {t(
                        "settings.price_list.prices.copy_prices_dialog.excluded_products_hint"
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      <strong>
                        {
                          seasonOptions.find(
                            (season) =>
                              String(season.id) ===
                              String(values.source_season_id)
                          )?.name
                        }
                      </strong>
                      {" → "}
                      <strong>
                        {
                          seasonOptions.find(
                            (season) =>
                              String(season.id) ===
                              String(values.target_season_id)
                          )?.name
                        }
                      </strong>
                    </p>
                    {preview.length === 0 ? (
                      <Alert variant="info">
                        {t(
                          "settings.price_list.prices.copy_prices_dialog.empty_source"
                        )}
                      </Alert>
                    ) : (
                      <>
                        <p>
                          {t(
                            "settings.price_list.prices.copy_prices_dialog.preview_summary",
                            {
                              count: preview.length,
                            }
                          )}
                        </p>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>
                                  {t(
                                    "settings.price_list.prices.copy_prices_dialog.table.category"
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "settings.price_list.prices.copy_prices_dialog.table.price_unit"
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "settings.price_list.prices.copy_prices_dialog.table.source_price"
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "settings.price_list.prices.copy_prices_dialog.table.target_price"
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.map((price, index) => (
                                <tr key={price.id}>
                                  <td>{price.parking_slot_category_name}</td>
                                  <td>
                                    {price.formatted_price_unit}
                                    {price.age_group_name && (
                                      <div>{price.age_group_name}</div>
                                    )}
                                  </td>
                                  <td>{euroFormatter(price.price)}</td>
                                  <td>
                                    <FormPriceInput
                                      name={`prices.${index}.price`}
                                      label={t(
                                        "settings.price_list.prices.copy_prices_dialog.price.label"
                                      )}
                                      disabled={isSubmitting}
                                      required
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {replacementCount > 0 && (
                          <Alert variant="warning">
                            <p>
                              {t(
                                "settings.price_list.prices.copy_prices_dialog.replacement_warning",
                                { count: replacementCount }
                              )}
                            </p>
                            <BTForm.Check
                              id="confirm-price-replacement"
                              name="replacement_confirmed"
                              label={t(
                                "settings.price_list.prices.copy_prices_dialog.replacement_confirmation"
                              )}
                              checked={values.replacement_confirmed}
                              onChange={handleChange}
                              disabled={isSubmitting}
                            />
                          </Alert>
                        )}
                      </>
                    )}
                  </>
                )}
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t("common:components.button.close")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                form="copy-prices-form"
                disabled={
                  isSubmitting ||
                  (preview !== null &&
                    (preview.length === 0 ||
                      (replacementCount > 0 && !values.replacement_confirmed)))
                }
              >
                {isSubmitting && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="mr-2"
                  />
                )}
                {preview === null
                  ? t(
                      "settings.price_list.prices.copy_prices_dialog.load_preview"
                    )
                  : t(
                      "settings.price_list.prices.copy_prices_dialog.copy_prices"
                    )}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default CopyPricesDialog;
